import clsx from 'clsx';
import { compact, each, isEmpty } from 'lodash';
import { func } from 'prop-types';
import { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { AddCartSuccessModal, Card, FormInputCount, LeisureContext, Loader } from 'components';
import { PAGE_TYPES } from 'constants';
import { formatLocalizedCurrency, getSubTypeLogo } from 'helpers';
import { useFetch, useScreenResize, useUser } from 'hooks';
import { leisureService } from 'services';
import { setCart } from 'store';

const LeisureSummary = () => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const dispatch = useDispatch();
  const { call: addToCartCall, loading: cartLoading } = useFetch();

  const { showElement, setShowElement } = useScreenResize();
  const { leisure, navigateToSearch, confirmationModal, setConfirmationModal } = useContext(LeisureContext);

  const prices = leisure.prices.salesPrices.map((price) => {
    const discount = leisure.prices.subsidizedPrices.find((subPrice) => subPrice.id === price.id)?.price ?? null;
    const subsidizedPrice = discount ? price.price - discount : null;
    return { ...price, subsidizedPrice };
  });

  const emptyPrices = isEmpty(leisure.prices.salesPrices);
  const emptySubsidizedPrices = isEmpty(leisure.prices.subsidizedPrices);
  const { leisurePricesResume, formatArticlePayload } = leisureService;

  const {
    register,
    getValues,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'all',
    defaultValues: {
      quantity: {}
    }
  });

  const providerValues = { register, getValues, setValue, errors, watch };

  const pricesResume = leisurePricesResume({ prices, quantity: getValues('quantity') });

  useEffect(() => {
    each(prices, (price) => setValue(`quantity.${price.id}`, 0));
  }, []);

  const getProductInfos = () => {
    let productsInfos = prices?.map((price) => {
      const quantity = getValues(`quantity.${price.id}`);
      if (quantity <= 0) return;
      const totalPrice = price?.subsidizedPrice || price?.price || 0;
      return {
        ...price,
        quantity: quantity || 0,
        total: totalPrice * quantity,
        vignette: leisure?.vignette || null,
        subTypeLogo: getSubTypeLogo(leisure?.subType)
      };
    });

    // Remove falsy values from the array
    productsInfos = compact(productsInfos);
    return productsInfos;
  };

  const addToCart = async () => {
    if (cartLoading || !currentUser?.serialNumber) return;
    setShowElement((prev) => ({ ...prev, mobile: false }));

    let payload = {
      articleId: leisure.productId,
      typeItem: leisure.typeItem,
      onPlan: 0,
      placement: []
    };

    // Add all articles with quantity > 0 to the cart
    payload = formatArticlePayload({
      quantity: getValues('quantity'),
      payload,
      leisure,
      prices
    });

    // Prevent submit if there are no articles placement in the cart
    if (isEmpty(payload) || cartLoading) return;

    const { data } =
      (await addToCartCall({
        service: 'checkout',
        method: 'post',
        path: `/${currentUser.serialNumber}/cart/ticketing/products`,
        data: { lines: payload }
      })) || {};

    if (data?.status === 200 && !isEmpty(data?.body)) {
      setConfirmationModal({
        open: true,
        content: getProductInfos(),
        alert: data.body?.alert || data.body?.notifications?.[0]?.message
      });
      dispatch(setCart(data.body));
      each(prices, (price) => setValue(`quantity.${price.id}`, 0));
    } else if (data?.status === 400) {
      toast.error(t('cart:addError'));
    }
  };

  return (
    <>
      <div
        className={clsx('leisure-overlay', showElement.mobile && 'open-mobile')}
        onClick={() => setShowElement((prev) => ({ ...prev, mobile: false }))}
      />

      <Card onMobile={false} onTablet={false} className={clsx('leisure-summary', showElement.mobile && 'open-mobile')}>
        {(showElement.mobile || showElement.desktop) && (
          <>
            <div className="leisure-summary-wrapper">
              <p className="uppercase font-semibold text-sm">{t('products:tickets')}</p>

              {emptyPrices && (
                <div className="py-4">
                  <Trans i18nKey="products:emptyPrices">
                    <button type="button" className="underline hover:no-underline" onClick={navigateToSearch}>
                      {t('clickHere')}
                    </button>
                  </Trans>
                </div>
              )}

              {prices.map((price) => (
                <div key={`leisure-price-${price.id}`} className="border-b border-gray-200 last:border-none py-4">
                  <p className="uppercase text-sm mb-2">{price.label}</p>

                  <div
                    className={clsx('flex justify-between', price.subsidizedPrice > 0 ? 'items-start' : 'items-center')}
                  >
                    <div>
                      {price.subsidizedPrice > 0 && <p className="text-xs">{t('products:pricesLegend')}</p>}

                      <div className="flex gap-2">
                        <p className={clsx(price.subsidizedPrice && 'line-through')}>
                          {formatLocalizedCurrency(price.price)}
                        </p>

                        {price.subsidizedPrice > 0 && (
                          <p className="text-primary-500">{formatLocalizedCurrency(price.subsidizedPrice)}</p>
                        )}
                      </div>
                    </div>

                    <FormProvider {...providerValues}>
                      <form>
                        <FormInputCount name={`quantity.${price.id}`} label={t('form:quantity')} rules={{ min: 0 }} />
                      </form>
                    </FormProvider>
                  </div>
                </div>
              ))}
            </div>

            {!emptyPrices && (
              <>
                <div className="border-t border-gray-200 pt-4 w-[calc(100%-4rem)] mx-auto print:w-full print:p-3">
                  {!emptySubsidizedPrices && (
                    <>
                      <div className="flex justify-between mb-2 gap-x-2 md:mb-4">
                        <p>{t('products:normalPrice')}</p>
                        <span className="font-bold">{pricesResume.total}</span>
                      </div>

                      <div className="flex justify-between mb-2 gap-x-2 md:mb-4">
                        <p>{t('products:employerPrice')}</p>
                        <span className="font-bold">-{pricesResume.employerDiscount}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between gap-x-2">
                    <p>{t('products:netPrice')}</p>
                    <span className="font-bold">{pricesResume.netPrice}</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn uppercase mx-auto mt-4 lg:my-8 print:hidden"
                  onClick={addToCart}
                  data-test-id="submitCartButton"
                  disabled={cartLoading}
                >
                  {cartLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
                  {t('products:addToCart')}
                </button>
              </>
            )}
          </>
        )}
      </Card>

      <div className={clsx('bg-white w-full lg:hidden print:hidden', showElement.mobile && 'hidden')}>
        <button
          type="button"
          className="btn mx-auto"
          onClick={() => setShowElement((prev) => ({ ...prev, mobile: true }))}
          disabled={cartLoading}
        >
          {cartLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
          {t('products:addToCart')}
        </button>
      </div>

      <AddCartSuccessModal
        confirmationModal={confirmationModal}
        setConfirmationModal={setConfirmationModal}
        pageType={PAGE_TYPES.leisures}
      />
    </>
  );
};

LeisureSummary.propTypes = {
  setCartAlert: func
};

export default LeisureSummary;
